/**
 * DebugOverlay — on-screen výpis chyb z konzole pro adminy se zapnutým debug módem.
 * Mountuje se jednou v `App` (self-gating: nic nerenderuje, pokud uživatel není admin
 * nebo je debug mód vypnutý). Slouží k ladění na mobilní PWA, kde nejsou DevTools.
 *
 * Fixed panel u spodního okraje (nad ostatními vrstvami), s vlastním scrollem, ať
 * nepřekáží zbytku stránky. Data plní `debug-log.ts`.
 */
import { useSyncExternalStore } from "react";
import { Bug, Trash2, X, ClipboardCopy } from "lucide-react";
import { hasRole, useAuth } from "../lib/use-auth.js";
import { useDebugMode } from "../lib/use-debug-mode.js";
import { setDebugMode } from "../lib/debug-mode.js";
import { getDebugEntries, subscribeDebugLog, clearDebugLog, type DebugEntry } from "../lib/debug-log.js";

function useDebugEntries(): DebugEntry[] {
  return useSyncExternalStore(subscribeDebugLog, getDebugEntries, getDebugEntries);
}

function fmtTime(t: number): string {
  const d = new Date(t);
  return d.toLocaleTimeString("cs-CZ", { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
}

function entryToText(e: DebugEntry): string {
  return `[${fmtTime(e.time)}] ${e.level.toUpperCase()} ${e.message}${e.stack ? "\n" + e.stack : ""}`;
}

export function DebugOverlay() {
  const { role } = useAuth();
  const debugOn = useDebugMode();
  const entries = useDebugEntries();

  if (!debugOn || !hasRole(role, "admin")) return null;

  async function copyAll() {
    const text = entries.map(entryToText).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // schránka nedostupná (insecure context) — tichý fail
    }
  }

  // Nejnovější nahoře.
  const ordered = [...entries].reverse();

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] max-h-[45vh] flex flex-col border-t border-status-error/40 bg-black/95 text-slate-200 shadow-2xl"
      role="region"
      aria-label="Debug konzole"
    >
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/10 bg-status-error/15">
        <Bug size={14} className="text-status-error shrink-0" />
        <span className="text-xs font-semibold tracking-wide">
          Debug konzole <span className="text-slate-400 font-normal">({entries.length})</span>
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => void copyAll()}
            disabled={entries.length === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-40"
            title="Kopírovat vše"
          >
            <ClipboardCopy size={13} /> Kopírovat
          </button>
          <button
            type="button"
            onClick={() => clearDebugLog()}
            disabled={entries.length === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-40"
            title="Vymazat"
          >
            <Trash2 size={13} /> Vymazat
          </button>
          <button
            type="button"
            onClick={() => setDebugMode(false)}
            className="inline-flex items-center justify-center p-1 rounded text-slate-300 hover:text-white hover:bg-white/10"
            title="Zavřít (vypnout debug mód)"
            aria-label="Zavřít debug konzoli"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto px-3 py-2 font-mono text-[11px] leading-relaxed">
        {ordered.length === 0 ? (
          <p className="text-slate-500 italic py-2">Zatím žádné chyby. Chyby z konzole se objeví zde.</p>
        ) : (
          <ul className="space-y-1.5">
            {ordered.map((e) => (
              <li key={e.id} className="border-b border-white/5 pb-1.5 last:border-0 break-words">
                <div className="flex items-start gap-2">
                  <span className="text-slate-500 shrink-0">{fmtTime(e.time)}</span>
                  <span
                    className={`shrink-0 font-semibold ${
                      e.level === "error" ? "text-status-error" : "text-amber-400"
                    }`}
                  >
                    {e.level === "error" ? "ERR" : "WARN"}
                  </span>
                  <span className="whitespace-pre-wrap text-slate-200">{e.message}</span>
                </div>
                {e.stack && (
                  <details className="mt-0.5 ml-[3.5rem]">
                    <summary className="cursor-pointer text-slate-500 hover:text-slate-300">stack</summary>
                    <pre className="mt-1 whitespace-pre-wrap text-slate-400">{e.stack}</pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
